import { format } from 'date-fns';
import {
  ArchiveBoxXMarkIcon,
  CheckBadgeIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../../component-lib/catalyst/dropdown';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../../../component-lib/catalyst/dialog';
import { FieldGroup } from '../../../component-lib/catalyst/fieldset';
import DatePicker from '../../../component-lib/Form/DatePicker';
import { Button } from '../../../component-lib/catalyst/button';
import SpinnerIcon from '../../../component-lib/icons/Spinner';
import ConfirmDialog from '../../../component-lib/ConfirmDialog/ConfirmDialog';
import { useState } from 'react';

export default function TaskDropdownMenu({
  task,
  reopenTaskMutation,
  completeTaskMutation,
  completedInThePastMutation,
  cloneTaskMutation,
  archiveTaskMutation,
  onAddTaskToGroupClick,
  onMoveTaskClick,
}) {
  const [isCompletedInThePastDialogOpen, setIsCompletedInThePastDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [taskToArchive, setTaskToArchive] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [cardId, setCardId] = useState(null);

  return (
    <>
      <Dropdown>
        <DropdownButton plain>
          <EllipsisVerticalIcon />
        </DropdownButton>
        <DropdownMenu>
          <DropdownItem
            onClick={() => {
              setTaskToArchive(task.id);
              setIsConfirmDialogOpen(true);
            }}
          >
            <ArchiveBoxXMarkIcon />
            <DropdownLabel>Archive {task.isGroup ? 'group' : 'task'}</DropdownLabel>
          </DropdownItem>
          {task.isGroup ? null : task.completed ? (
            <DropdownItem onClick={() => reopenTaskMutation.mutate({ cardId: task.id })}>
              <EyeIcon />
              <DropdownLabel>Reopen task</DropdownLabel>
            </DropdownItem>
          ) : (
            <>
              <DropdownItem onClick={() => completeTaskMutation.mutate({ cardId: task.id })}>
                <CheckBadgeIcon />
                <DropdownLabel>Mark as complete</DropdownLabel>
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  setCardId(task.id);
                  setStartDate(new Date(task.startDate));
                  setEndDate(new Date());
                  setIsCompletedInThePastDialogOpen(true);
                }}
              >
                <ClockIcon />
                <DropdownLabel>Completed in the past</DropdownLabel>
              </DropdownItem>
            </>
          )}
          {task.isGroup ? null : (
            <DropdownItem
              onClick={() => {
                cloneTaskMutation.mutate({
                  cardId: task.id,
                });
              }}
            >
              <DocumentDuplicateIcon />
              <DropdownLabel>Clone task</DropdownLabel>
            </DropdownItem>
          )}
          {task.isGroup && (
            <DropdownItem
              onClick={() => {
                onAddTaskToGroupClick({
                  id: task.id,
                  name: task.name,
                  subtasks: task.subtasks,
                });
              }}
            >
              <PlusIcon />
              <DropdownLabel>Add task to group</DropdownLabel>
            </DropdownItem>
          )}
          {!task.isGroup && (
            <DropdownItem
              onClick={() => {
                onMoveTaskClick(task);
              }}
            >
              <ArrowsRightLeftIcon />
              <DropdownLabel>Move to group</DropdownLabel>
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
      <Dialog
        open={isCompletedInThePastDialogOpen}
        onClose={() => setIsCompletedInThePastDialogOpen(false)}
      >
        <DialogTitle>
          <div className="flex justify-between">
            <span>Update task start and end dates</span>
            <button onClick={() => setIsCompletedInThePastDialogOpen(false)}>
              <XMarkIcon className="size-6" />
            </button>
          </div>
        </DialogTitle>
        <DialogBody>
          <FieldGroup>
            <DatePicker
              value={startDate}
              label="Start date"
              onChange={(date) => setStartDate(date)}
            />

            <DatePicker
              value={endDate}
              label="End date"
              onChange={(date) => setEndDate(date)}
            />
          </FieldGroup>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setIsCompletedInThePastDialogOpen(false)}
            disabled={completedInThePastMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              completedInThePastMutation.mutate({
                cardId,
                startDate: format(startDate, 'yyyy-MM-dd'),
                endDate: format(endDate, 'yyyy-MM-dd'),
              });
              setIsCompletedInThePastDialogOpen(false);
            }}
            disabled={completedInThePastMutation.isPending}
          >
            {completedInThePastMutation.isPending && (
              <SpinnerIcon className="animate-spin w-5 h-5" />
            )}
            {completedInThePastMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        setIsOpen={() => {
          setTaskToArchive(null);
          setIsConfirmDialogOpen(false);
        }}
        title="Confirm archiving task"
        message="Are you sure you want to archive this task? Archived tasks cannot be restored."
        confirmText="Archive"
        confirmTextProgress="Archiving..."
        onConfirm={() => {
          archiveTaskMutation.mutate(
            { cardId: taskToArchive },
            {
              onSuccess: () => {
                setTaskToArchive(null);
                setIsConfirmDialogOpen(false);
              },
            },
          );
        }}
        onCancel={() => {
          setIsConfirmDialogOpen(false);
          setTaskToArchive(null);
        }}
        isPending={archiveTaskMutation.isPending}
      />
    </>
  );
}
